import { io } from 'socket.io-client';
import eCoachVMSApi from '../Api';

class NotificationService {
    constructor() {
        this.socket = null;
        this.pollingInterval = null;
        this.listeners = new Set();
        this.isConnected = false;
        this.userId = null;
        this.useWebSocket = true; // Default to WebSocket
        this.api = eCoachVMSApi().baseURL;
        this.url = window._env_?.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_WEBSOCKET_URL; // Socket.IO server URL
        this.disconnect = this.disconnect.bind(this);
        this.hostname = window.location.hostname;
        this.reconnectFailed = false; // Flag to track retry exhaustion
    }

    //==============================================================
    // ✅ Initialize based on user settings
    //==============================================================
    async init(userId, useWebSocket = true) {
        this.session = localStorage.getItem('session') || sessionStorage.getItem('session');
        this.useWebSocket = useWebSocket;
        this.userId = userId;
        this.reconnectFailed = false; // Reset retry flag on init

        // Stop any existing socket or polling before starting new mode
        this.disconnect();

        if (this.useWebSocket) {
            this._initSocketIO();
        } else {
            this._startPolling();
        }
    }

    //==============================================================
    // ✅ Initialize Socket.IO connection and event handlers
    //==============================================================
    _initSocketIO() {
        console.log('_initSocketIO() called');
        console.log('Socket target URL:', this.url);
        console.log('Hostname sent to backend:', this.hostname);
        console.log('Session token:', this.session);

        // Initialize Socket.IO connection with options
        this.socket = io(this.url, {
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            transports: ['websocket'], // or ['polling', 'websocket']
            withCredentials: true,
            auth: {
                session: this.session,
                hostname: this.hostname
            }
        });

        // Connection established
        this.socket.on('connect', async () => {
            console.log('✅ Socket.IO connected with ID:', this.socket.id);
            this.isConnected = true;
            this.reconnectFailed = false; // Reset retry flag on successful connection

            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }

            // ======================================================
            // One-time initial fetch of unread notifications
            // ======================================================
            try {
                const response = await this.api.get(`/notifications/user?user_id=${this.userId}&is_read=false`);
                if (Array.isArray(response.data) && response.data.length > 0) {
                    console.log(`📦 Initial ${response.data.length} notifications fetched`);
                    response.data.forEach((n) => this._notifyListeners(n));
                } else {
                    console.log('📦 No unread notifications found on connect');
                }
            } catch (err) {
                console.error('❌ Failed to fetch initial notifications:', err.message);
            }
        });

        // Handle custom notification event
        this.socket.on('notification', (data) => {
            console.log('🔔 Notification received from backend:', data);
            this._notifyListeners(data);
        });

        // Handle disconnect
        this.socket.on('disconnect', (reason) => {
            console.warn('⚠️ Socket.IO disconnected:', reason);
            this.isConnected = false;
            if (reason !== 'io client disconnect' && this.hasExhaustedReconnects) {
                console.log('⚠️ All reconnection attempts failed, falling back to polling');
                this._startPolling();
            }
        });

        // Handle connection errors
        this.socket.on('connect_error', (error) => {
            console.error('❌ Socket.IO connect_error:', error.message, error);
        });

        this.socket.on('reconnect_failed', () => {
            console.warn('⚠️ All reconnection attempts failed');
            this.reconnectFailed = true;
            this._startPolling();
        });

        // Optional: Handle successful reconnection
        this.socket.on('reconnect', (attemptNumber) => {
            console.log(`♻️ Successfully reconnected after ${attemptNumber} attempts`);
            this.reconnectFailed = false;
        });
    }

    //==============================================================
    // ✅ Polling fallback (used if WebSocket fails)
    //==============================================================
    _startPolling() {
        this.useWebSocket = false;
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }

        this.pollingInterval = setInterval(async () => {
            try {
                const response = await this.api.get(`/notifications/user/?user_id=${this.userId}&is_read=false&is_delivered=false`);
                if (response.data.length > 0) {
                    response.data.forEach((notification) => {
                        this._notifyListeners(notification);
                    });
                }
            } catch (error) {
                console.error('Polling error:', error);
                this.disconnect();
            }
        }, 60000); // Poll every 60 seconds
        console.log('started pooling for notifications');
    }

    //==============================================================
    // ✅ Notify all active listeners
    //==============================================================
    _notifyListeners(notification) {
        this.listeners.forEach((listener) => listener(notification));
    }

    //==============================================================
    // ✅ Add a listener and return cleanup function
    //==============================================================
    addListener(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback); // Cleanup function
    }

    //==============================================================
    // ✅ Disconnect and clean up
    //==============================================================
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.reconnectFailed = false; // Reset retry flag on disconnect
            this.socket = null;
        }
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.listeners.clear();
        this.isConnected = false;
    }
}

export const notificationService = new NotificationService();
